import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NewsletterRecord } from './app.types';

interface CustomNewsletterContent extends Record<string, unknown> {
  editor: 'wahl-custom-newsletter';
  version: 1;
  headline: string;
  intro: string;
  sectionTitle: string;
  bullets: string[];
  ctaLabel: string;
  ctaUrl: string;
  accentColor: string;
  backgroundColor: string;
}

@Component({
  selector: 'app-newsletter-editor',
  standalone: false,
  styles: [`
    .newsletter-custom-editor {
      display: block;
    }

    .newsletter-preview {
      min-height: 100%;
      padding: 24px;
      background: var(--newsletter-preview-background, #f4f7fb);
      border: 1px solid var(--ion-color-step-150, #d8e0ea);
    }

    .newsletter-preview-card {
      max-width: 640px;
      margin: 0 auto;
      background: #ffffff;
      border: 1px solid var(--ion-color-step-150, #d8e0ea);
    }

    .newsletter-preview-inner {
      padding: 32px;
      font-family: Arial, sans-serif;
    }

    .newsletter-preview h1,
    .newsletter-preview h2,
    .newsletter-preview p {
      margin-top: 0;
    }

    .newsletter-preview h1 {
      margin-bottom: 12px;
      color: #111827;
      font-size: 28px;
      line-height: 1.2;
    }

    .newsletter-preview h2 {
      margin-bottom: 10px;
      color: #111827;
      font-size: 18px;
      line-height: 1.3;
    }

    .newsletter-preview p,
    .newsletter-preview li {
      color: #374151;
      line-height: 1.5;
    }

    .newsletter-preview ul {
      margin: 0 0 20px;
      padding-left: 20px;
    }

    .newsletter-preview a {
      display: inline-block;
      padding: 12px 18px;
      background: var(--newsletter-accent-color, #164c63);
      color: #ffffff;
      text-decoration: none;
    }
  `],
  template: `
    <section class="preview newsletter-editor-shell newsletter-custom-editor" [hidden]="!visible">
      <ion-grid>
        <ion-row>
          <ion-col size="12" size-lg="5">
            <ion-list lines="full" [formGroup]="form">
              <ion-item>
                <ion-label position="stacked">Headline</ion-label>
                <ion-input formControlName="headline"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Intro</ion-label>
                <ion-textarea formControlName="intro" autoGrow="true" rows="3"></ion-textarea>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Section Title</ion-label>
                <ion-input formControlName="sectionTitle"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Bullet Points</ion-label>
                <ion-textarea formControlName="bulletsText" autoGrow="true" rows="5" placeholder="One point per line"></ion-textarea>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Button Text</ion-label>
                <ion-input formControlName="ctaLabel"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Button URL</ion-label>
                <ion-input type="url" formControlName="ctaUrl"></ion-input>
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Accent Color</ion-label>
                <input type="color" formControlName="accentColor" aria-label="Newsletter accent color">
              </ion-item>
              <ion-item>
                <ion-label position="stacked">Background Color</ion-label>
                <input type="color" formControlName="backgroundColor" aria-label="Newsletter background color">
              </ion-item>
            </ion-list>
          </ion-col>
          <ion-col size="12" size-lg="7">
            <section
              class="newsletter-preview"
              [style.--newsletter-preview-background]="draft().backgroundColor"
              [style.--newsletter-accent-color]="draft().accentColor"
              aria-label="Newsletter preview"
            >
              <article class="newsletter-preview-card">
                <div class="newsletter-preview-inner">
                  <h1>{{ draft().headline }}</h1>
                  <p>{{ draft().intro }}</p>
                  <h2>{{ draft().sectionTitle }}</h2>
                  <ul>
                    <li *ngFor="let bullet of draft().bullets">{{ bullet }}</li>
                  </ul>
                  <a [href]="draft().ctaUrl" target="_blank" rel="noopener noreferrer">{{ draft().ctaLabel }}</a>
                </div>
              </article>
            </section>
          </ion-col>
        </ion-row>
      </ion-grid>
    </section>
  `
})
export class NewsletterEditorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() newsletter: NewsletterRecord | null = null;
  @Output() contentChanged = new EventEmitter<void>();

  private readonly fb = new FormBuilder();
  private readonly defaultContent = this.defaultDraft();
  private loadedNewsletterId: number | null = null;
  private isApplyingNewsletter = false;
  private changesSubscription?: Subscription;

  readonly form = this.fb.nonNullable.group({
    headline: [this.defaultContent.headline],
    intro: [this.defaultContent.intro],
    sectionTitle: [this.defaultContent.sectionTitle],
    bulletsText: [this.defaultContent.bullets.join('\n')],
    ctaLabel: [this.defaultContent.ctaLabel],
    ctaUrl: [this.defaultContent.ctaUrl],
    accentColor: [this.defaultContent.accentColor],
    backgroundColor: [this.defaultContent.backgroundColor]
  });

  ngOnInit(): void {
    this.changesSubscription = this.form.valueChanges.subscribe(() => {
      if (!this.isApplyingNewsletter) {
        this.contentChanged.emit();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['newsletter']) {
      this.loadNewsletter(this.newsletter);
    }
  }

  ngOnDestroy(): void {
    this.changesSubscription?.unsubscribe();
  }

  snapshot(): { content: Record<string, unknown>; html: string; css: string } {
    const content = this.draft();

    return {
      content,
      html: this.htmlFor(content),
      css: ''
    };
  }

  clear(): void {
    this.loadedNewsletterId = null;
    this.applyDraft(this.defaultDraft());
  }

  acceptCurrentNewsletter(newsletterId: number): void {
    this.loadedNewsletterId = newsletterId;
  }

  draft(): CustomNewsletterContent {
    const value = this.form.getRawValue();

    return {
      editor: 'wahl-custom-newsletter',
      version: 1,
      headline: value.headline.trim() || this.defaultContent.headline,
      intro: value.intro.trim() || this.defaultContent.intro,
      sectionTitle: value.sectionTitle.trim() || this.defaultContent.sectionTitle,
      bullets: this.bulletsFromText(value.bulletsText),
      ctaLabel: value.ctaLabel.trim() || this.defaultContent.ctaLabel,
      ctaUrl: value.ctaUrl.trim() || this.defaultContent.ctaUrl,
      accentColor: this.hexColor(value.accentColor, this.defaultContent.accentColor),
      backgroundColor: this.hexColor(value.backgroundColor, this.defaultContent.backgroundColor)
    };
  }

  private loadNewsletter(newsletter: NewsletterRecord | null): void {
    if (!newsletter) {
      if (this.loadedNewsletterId !== null) {
        this.clear();
      }
      return;
    }

    if (this.loadedNewsletterId === newsletter.id) {
      return;
    }

    this.loadedNewsletterId = newsletter.id;
    this.applyDraft(this.contentFromNewsletter(newsletter));
  }

  private applyDraft(content: CustomNewsletterContent): void {
    this.isApplyingNewsletter = true;
    this.form.reset({
      headline: content.headline,
      intro: content.intro,
      sectionTitle: content.sectionTitle,
      bulletsText: content.bullets.join('\n'),
      ctaLabel: content.ctaLabel,
      ctaUrl: content.ctaUrl,
      accentColor: content.accentColor,
      backgroundColor: content.backgroundColor
    });
    setTimeout(() => {
      this.isApplyingNewsletter = false;
    });
  }

  private contentFromNewsletter(newsletter: NewsletterRecord): CustomNewsletterContent {
    if (this.isCustomContent(newsletter.content)) {
      return newsletter.content;
    }

    return {
      ...this.defaultDraft(),
      headline: newsletter.title || this.defaultContent.headline,
      intro: this.textFromHtml(newsletter.html || '') || this.defaultContent.intro
    };
  }

  private isCustomContent(value: Record<string, unknown>): value is CustomNewsletterContent {
    return value['editor'] === 'wahl-custom-newsletter' && value['version'] === 1;
  }

  private bulletsFromText(value: string): string[] {
    const bullets = value.split(/\r?\n/).map((line) => line.trim()).filter(Boolean).slice(0, 8);
    return bullets.length > 0 ? bullets : this.defaultContent.bullets;
  }

  private htmlFor(content: CustomNewsletterContent): string {
    const bullets = content.bullets.map((bullet) => `<li style="margin:0 0 8px;">${this.escapeHtml(bullet)}</li>`).join('');

    return `
      <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="font-family: Arial, sans-serif; background:${content.backgroundColor}; padding:24px;">
        <tr>
          <td align="center">
            <table width="640" cellpadding="0" cellspacing="0" role="presentation" style="max-width:640px; width:100%; background:#ffffff; border:1px solid #d8e0ea;">
              <tr>
                <td style="padding:32px;">
                  <h1 style="margin:0 0 12px; color:#111827; font-size:28px; line-height:1.2;">${this.escapeHtml(content.headline)}</h1>
                  <p style="margin:0 0 20px; color:#374151; line-height:1.5;">${this.escapeHtml(content.intro)}</p>
                  <h2 style="margin:0 0 10px; color:#111827; font-size:18px; line-height:1.3;">${this.escapeHtml(content.sectionTitle)}</h2>
                  <ul style="margin:0 0 20px; padding-left:20px; color:#374151; line-height:1.5;">${bullets}</ul>
                  <a href="${this.escapeHtml(content.ctaUrl)}" style="display:inline-block; padding:12px 18px; background:${content.accentColor}; color:#ffffff; text-decoration:none;">${this.escapeHtml(content.ctaLabel)}</a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    `;
  }

  private textFromHtml(html: string): string {
    const documentFragment = document.implementation.createHTMLDocument('');
    documentFragment.documentElement.innerHTML = html;
    return documentFragment.body.textContent?.replace(/\s+/g, ' ').trim().slice(0, 220) || '';
  }

  private hexColor(value: string, fallback: string): string {
    return /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private defaultDraft(): CustomNewsletterContent {
    return {
      editor: 'wahl-custom-newsletter',
      version: 1,
      headline: 'Newsletter headline',
      intro: 'Add campaign copy, images, offers, and calls to action.',
      sectionTitle: 'Highlights',
      bullets: [
        'Share the most important customer benefit.',
        'Keep the offer clear and easy to scan.',
        'Give readers one direct next step.'
      ],
      ctaLabel: 'Call to action',
      ctaUrl: 'https://example.com',
      accentColor: '#164c63',
      backgroundColor: '#f4f7fb'
    };
  }
}
